import React, { createContext, useContext, useMemo, useEffect } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { LOCALE_PATH_PREFIXES, getLocaleByPathPrefix, DEFAULT_LOCALE } from '../config/locales';
import { setApiLocale } from '../services/http';

const LocaleContext = createContext(null);

export function useLocale() {
  const ctx = useContext(LocaleContext);
  if (!ctx) {
    return {
      locale: DEFAULT_LOCALE,
      pathPrefix: '',
      prependPath: (path) => (path && path.startsWith('/') ? path : `/${path || ''}`),
      getPathForLocale: (newPathPrefix) => {
        const { pathname } = window.location;
        const base = pathname;
        return newPathPrefix ? `/${newPathPrefix}${base.startsWith('/') ? base : `/${base}`}` : base;
      },
    };
  }
  return ctx;
}

function stripPrefix(pathname, pathPrefix) {
  if (!pathPrefix) return pathname;
  const prefix = `/${pathPrefix}`;
  if (pathname === prefix) return '/';
  if (pathname.startsWith(prefix + '/')) return pathname.slice(prefix.length);
  return pathname;
}

function buildPath(basePath, pathPrefix) {
  const path = basePath.startsWith('/') ? basePath : `/${basePath}`;
  if (!pathPrefix) return path;
  return `/${pathPrefix}${path}`;
}

function ApiLocaleSync() {
  const ctx = useContext(LocaleContext);
  useEffect(() => {
    setApiLocale(ctx?.locale?.code ?? '');
  }, [ctx?.locale?.code]);
  return null;
}

export function LocaleProvider({ pathPrefix, children }) {
  const location = useLocation();
  const locale = useMemo(() => getLocaleByPathPrefix(pathPrefix), [pathPrefix]);
  const value = useMemo(() => {
    const basePath = stripPrefix(location.pathname, pathPrefix);
    return {
      locale,
      pathPrefix: locale.pathPrefix,
      prependPath: (path) => buildPath(path, locale.pathPrefix),
      getPathForLocale: (newPathPrefix) => {
        const base = stripPrefix(location.pathname, pathPrefix);
        return buildPath(base, newPathPrefix);
      },
    };
  }, [locale, pathPrefix, location.pathname]);
  return (
    <LocaleContext.Provider value={value}>
      <ApiLocaleSync />
      {children}
    </LocaleContext.Provider>
  );
}

export function useLocalePrefixParam() {
  const params = useParams();
  return params.localePrefix || '';
}

/** Returns true if the given pathPrefix is a valid locale prefix. */
export function isValidLocalePrefix(prefix) {
  return LOCALE_PATH_PREFIXES.includes(prefix);
}
